import { Link } from "react-router-dom";
import { ArrowRight, BellRing, Building2, Check, ClipboardCheck, Database, Gavel } from "lucide-react";
import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";

const features = [
    {
        icon: BellRing,
        title: "Real-time Alerts",
        body: "Configure alerts per property, per portfolio or per violation class. Delivered by email or SMS when Violerts detects new or updated records.",
        to: "/alerts",
        peek: [
            "Alerts in your inbox in every plan, texts on your phone at +$19.99/mo",
            "Alerts free with an account — notifications with any paid plan",
            "Real alert emails and HPD violation texts on the page",
        ],
    },
    {
        icon: Gavel,
        title: "Violation Resolution",
        body: "Each violation opens with a cure path: the exact filings required, hearing windows, penalty exposure, and certification steps to close it out.",
        to: "/violation-resolution",
        peek: [
            "Works hand-in-hand with Building Violation Solutions Inc.",
            "Request a resolution from your dashboard, alert emails or texts",
            "The full path from citation to certified close-out",
        ],
    },
    {
        icon: Building2,
        title: "Reports",
        body: "Comprehensive Property Reports and Property Status Reports, plus Airtable sync to keep your own tables current.",
        to: "/reports",
        peek: [
            "CPR — Comprehensive Property Report",
            "PSR — Property Status Report",
            "Request either report straight from the page",
            "Sync every report to Airtable",
        ],
    },
    {
        icon: ClipboardCheck,
        title: "Compliance",
        body: "Track inspections, local law deadlines and portfolio-wide obligations across every building you own or manage.",
        to: "/compliance",
        peek: [
            "Every inspection and filing deadline in one calendar",
            "Local law obligations tracked per building",
            "How compliance tracking works, step by step",
        ],
    },
    {
        icon: Building2,
        title: "Property Data",
        body: "One feed across DOB, HPD, ECB/OATH, FDNY, DEP, DOT and every other office that can cite your building.",
        to: "/property-data",
        peek: [
            "DOB, HPD, ECB/OATH, FDNY, DEP, DOT, DDC and more",
            "18 violation types, 13 infrastructure systems, 9 certifications",
            "Every penalty and refund in one clear view",
        ],
    },
    {
        icon: Database,
        title: "Historical Data",
        body: "Decades of filing, complaint, inspection and penalty history per property — exportable for diligence, underwriting or litigation.",
        to: "/historical-data",
        peek: [
            "Record types spanning decades, not just what's open today",
            "Filing, complaint, inspection and penalty history",
            "Built for diligence, underwriting and litigation",
        ],
    },
];
function Features() {
    return (<>
      <PageHero title="Everything Violerts does, in one place." description="From the first alert to a certified close-out — explore each part of the platform."/>
      <Section>
        <SectionHeading title="What the platform does"/>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (<Link key={f.title} to={f.to} className="group relative flex min-h-[300px] flex-col overflow-hidden rounded-xl border border-border bg-card p-7 shadow-card transition-all hover:-translate-y-1 hover:border-signal/30 hover:shadow-lift">
              <f.icon className="h-5 w-5 text-foreground"/>
              <h2 className="mt-5 text-lg font-semibold">{f.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-medium text-foreground">
                Learn more
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1"/>
              </span>
              <div className="absolute inset-0 flex translate-y-3 flex-col rounded-xl bg-ink p-7 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-signal">Inside this page</span>
                <h3 className="mt-3 text-lg font-semibold text-ink-foreground">{f.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {f.peek.map((item) => (<li key={item} className="flex gap-2.5 text-[13px] leading-relaxed text-ink-foreground/75">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-signal"/>
                      {item}
                    </li>))}
                </ul>
                <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-medium text-signal">
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1"/>
                </span>
              </div>
            </Link>))}
        </div>
      </Section>
      <PageCta />
    </>);
}

export default Features;
